import {
    linkGuardianToStudent,
    unlinkGuardianFromStudent,
    updateGuardianStudentLink,
} from '../services/UserService';
import {DEFAULT_RELATIONSHIP_TYPE} from '../constants/relationshipTypes';

export interface LinkOption {
    value: string;
    relationshipType: string;
}

export interface LinkChanges {
    toUnlink: LinkOption[];
    toUpdate: LinkOption[];
    toLink: LinkOption[];
}

export interface InlineAttachedUser {
    firstName?: string;
    lastName?: string;
    otherNames?: string;
    email?: string;
    phoneNo?: string;
    gender?: string;
    dob?: string;
    relationshipType?: string;
}

type CreateUserFn = (payload: Record<string, unknown>) => Promise<unknown>;

export function computeLinkChanges(
    original: Record<string, string>,
    current: LinkOption[],
): LinkChanges {
    const originalIds = new Set(Object.keys(original));
    const currentIds = new Set(current.map((item) => item.value));

    const toUnlink: LinkOption[] = [];
    for (const userId of originalIds) {
        if (!currentIds.has(userId)) {
            toUnlink.push({
                value: userId,
                relationshipType: original[userId] ?? DEFAULT_RELATIONSHIP_TYPE,
            });
        }
    }

    const toUpdate: LinkOption[] = [];
    const toLink: LinkOption[] = [];
    for (const item of current) {
        if (!originalIds.has(item.value)) {
            toLink.push(item);
            continue;
        }
        const originalType = original[item.value] ?? DEFAULT_RELATIONSHIP_TYPE;
        if (item.relationshipType !== originalType) {
            toUpdate.push(item);
        }
    }

    return {toUnlink, toUpdate, toLink};
}

export async function applyLinkChanges({
    parentId,
    isStudentMode,
    changes,
}: {
    parentId: string;
    isStudentMode: boolean;
    changes: LinkChanges;
}): Promise<void> {
    for (const item of changes.toUnlink) {
        const studentId = isStudentMode ? parentId : item.value;
        const guardianId = isStudentMode ? item.value : parentId;
        await unlinkGuardianFromStudent(studentId, guardianId);
    }

    for (const item of changes.toUpdate) {
        const studentId = isStudentMode ? parentId : item.value;
        const guardianId = isStudentMode ? item.value : parentId;
        await updateGuardianStudentLink(studentId, guardianId, item.relationshipType);
    }

    for (const item of changes.toLink) {
        const studentId = isStudentMode ? parentId : item.value;
        const guardianId = isStudentMode ? item.value : parentId;
        await linkGuardianToStudent(studentId, guardianId, item.relationshipType);
    }
}

export async function createInlineAttachedUsers({
    parentId,
    isStudentMode,
    attachedUsers,
    addStudent,
    addGuardian,
}: {
    parentId: string;
    isStudentMode: boolean;
    attachedUsers: InlineAttachedUser[];
    addStudent: CreateUserFn;
    addGuardian: CreateUserFn;
}): Promise<void> {
    for (const attached of attachedUsers) {
        const relationshipType = attached.relationshipType ?? DEFAULT_RELATIONSHIP_TYPE;
        const payload = {
            firstName: attached.firstName ?? '',
            lastName: attached.lastName ?? '',
            otherNames: attached.otherNames ?? '',
            email: attached.email ?? '',
            phoneNo: attached.phoneNo ?? '',
            gender: attached.gender ?? '',
            dob: attached.dob ?? '',
            sendInvite: false,
            sendInviteAttached: false,
        };

        if (isStudentMode) {
            await addGuardian({
                ...payload,
                studentLinks: [{student_id: parentId, relationship_type: relationshipType}],
            });
        } else {
            await addStudent({
                ...payload,
                guardianLinks: [{guardian_id: parentId, relationship_type: relationshipType}],
            });
        }
    }
}
